import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  type JSX,
  type Ref,
  type RefCallback,
  type TextareaHTMLAttributes,
} from 'react';
import clsx from 'clsx';
import { FormField, type FormFieldProps } from './FormField';

function mergeRefs<T>(...refs: Array<Ref<T>>): RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref !== null) {
        (ref as { current: T | null }).current = value;
      }
    });
  };
}

export type TextareaFieldProps = Omit<FormFieldProps, 'children' | 'onChange' | 'onBlur'> &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'children'> & {
    textareaClassName?: string;
  };

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  function TextareaField(
    {
      className,
      textareaClassName,
      label,
      description,
      error,
      required,
      hint,
      labelSecondary,
      ...textareaProps
    },
    forwardedRef,
  ): JSX.Element {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const mergedRef = useMemo(() => mergeRefs(forwardedRef, internalRef), [forwardedRef]);

    useEffect(() => {
      if (!internalRef.current) return;
      const message = typeof error === 'string' && error.length > 0 ? error : '';
      internalRef.current.setCustomValidity(message);
    }, [error]);

    return (
      <FormField
        id={textareaProps.id}
        label={label}
        description={description}
        error={error}
        required={required}
        hint={hint}
        labelSecondary={labelSecondary}
        className={className}
      >
        <textarea
          ref={mergedRef}
          required={required}
          className={clsx(
            'w-full rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm',
            textareaClassName,
          )}
          {...textareaProps}
        />
      </FormField>
    );
  },
);
